import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Mail, Phone, Calendar, Users, MapPin, Euro, MessageSquare, ShieldCheck, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/lib/use-auth";
import {
  listMyContactRequests, replyToContactRequest, updateContactRequestStatus,
} from "@/lib/contact-requests.functions";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/dashboard/richieste")({
  head: () => ({ meta: [{ title: "Richieste — Dashboard ChefConnect" }] }),
  component: RichiesteePage,
});

type Req = Awaited<ReturnType<typeof listMyContactRequests>>[number];

const statusLabel: Record<string, { label: string; cls: string }> = {
  new: { label: "Nuova", cls: "bg-gold/20 text-gold border-gold/40" },
  viewed: { label: "Vista", cls: "bg-muted text-foreground border-border" },
  replied: { label: "Risposto", cls: "bg-green-500/15 text-green-600 border-green-500/30" },
  closed: { label: "Chiusa", cls: "bg-muted/50 text-muted-foreground border-border" },
};

function RichiesteePage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const listFn = useServerFn(listMyContactRequests);
  const replyFn = useServerFn(replyToContactRequest);
  const statusFn = useServerFn(updateContactRequestStatus);

  const q = useQuery({
    enabled: !!user,
    queryKey: ["contact-requests", user?.id],
    queryFn: () => listFn(),
  });

  const gate = useQuery({
    enabled: !!user,
    queryKey: ["richieste-gate", user?.id],
    queryFn: async () => {
      const [chef, listings] = await Promise.all([
        supabase.from("chefs").select("verified_badge").eq("user_id", user!.id).maybeSingle(),
        supabase.from("chef_listings").select("id", { count: "exact", head: true }).eq("chef_id", user!.id),
      ]);
      return {
        verified: chef.data?.verified_badge === true,
        listings: listings.count ?? 0,
      };
    },
  });

  const [openId, setOpenId] = useState<string | null>(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const sendReply = async (id: string) => {
    if (reply.trim().length < 2) return;
    setBusy(true);
    try {
      await replyFn({ data: { id, reply: reply.trim() } });
      toast.success("Risposta inviata al cliente");
      setReply("");
      setOpenId(null);
      await qc.invalidateQueries({ queryKey: ["contact-requests", user?.id] });
      qc.invalidateQueries({ queryKey: ["my-notifications"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Errore");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id: string, status: "viewed" | "closed") => {
    try {
      await statusFn({ data: { id, status } });
      await qc.invalidateQueries({ queryKey: ["contact-requests", user?.id] });
      qc.invalidateQueries({ queryKey: ["my-notifications"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Errore");
    }
  };

  const requests: Req[] = q.data ?? [];
  const asChef = requests.filter((r) => r.chef_id === user?.id);
  const asClient = requests.filter((r) => r.client_id === user?.id);
  const verified = gate.data?.verified ?? false;
  const hasListings = (gate.data?.listings ?? 0) > 0;

  return (
    <main className="container mx-auto max-w-4xl px-4 py-8">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Dashboard
      </Link>
      <h1 className="mt-4 font-display text-3xl">Richieste di contatto</h1>
      <p className="text-muted-foreground mt-1">Gestisci preventivi e prenotazioni dei tuoi clienti.</p>

      {q.isLoading && <p className="mt-8 text-muted-foreground">Caricamento…</p>}

      {!q.isLoading && requests.length === 0 && (
        <div className="mt-10 rounded-xl border border-dashed border-border bg-card p-8">
          <div className="mx-auto max-w-xl text-center">
            <MessageSquare className="mx-auto h-10 w-10 text-muted-foreground" />
            <h2 className="mt-4 font-display text-2xl">Nessuna richiesta disponibile</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Le richieste dei clienti compariranno qui dopo che:
            </p>
            <ul className="mt-3 inline-flex flex-col gap-1 text-sm text-muted-foreground">
              <li>{verified ? "✅" : "⏳"} la tua verifica sarà approvata</li>
              <li>{hasListings ? "✅" : "⏳"} avrai pubblicato almeno un annuncio</li>
            </ul>
            <div className="mt-6 flex justify-center">
              {!verified ? (
                <Button asChild className="bg-gold text-gold-foreground hover:bg-gold/90">
                  <Link to="/dashboard/verifica"><ShieldCheck className="mr-2 h-4 w-4" />Vai alla verifica</Link>
                </Button>
              ) : !hasListings ? (
                <Button asChild className="bg-gold text-gold-foreground hover:bg-gold/90">
                  <Link to="/dashboard/annunci"><Megaphone className="mr-2 h-4 w-4" />Pubblica il tuo primo annuncio</Link>
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {asChef.length > 0 && (
        <section className="mt-8">
          <h2 className="font-display text-xl mb-3">Ricevute ({asChef.length})</h2>
          <div className="space-y-4">
            {asChef.map((r) => (
              <RequestCard
                key={r.id}
                req={r}
                role="chef"
                onOpen={() => { setOpenId(openId === r.id ? null : r.id); setReply(r.chef_reply ?? ""); if (r.status === "new") void setStatus(r.id, "viewed"); }}
                expanded={openId === r.id}
                reply={reply}
                setReply={setReply}
                onReply={() => sendReply(r.id)}
                onClose={() => setStatus(r.id, "closed")}
                busy={busy}
              />
            ))}
          </div>
        </section>
      )}

      {asClient.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-xl mb-3">Inviate ({asClient.length})</h2>
          <div className="space-y-4">
            {asClient.map((r) => (
              <RequestCard key={r.id} req={r} role="client" />
            ))}
          </div>
        </section>
      )}
    </main>
  );
}

function RequestCard({
  req, role, onOpen, expanded, reply, setReply, onReply, onClose, busy,
}: {
  req: Req;
  role: "chef" | "client";
  onOpen?: () => void;
  expanded?: boolean;
  reply?: string;
  setReply?: (s: string) => void;
  onReply?: () => void;
  onClose?: () => void;
  busy?: boolean;
}) {
  const s = statusLabel[req.status] ?? statusLabel.new;
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium">{req.client_name}</p>
            <Badge variant="outline" className={s.cls}>{s.label}</Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {new Date(req.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
          </p>
        </div>
        {role === "chef" && (
          <Button size="sm" variant="outline" onClick={onOpen}>
            {expanded ? "Chiudi" : "Apri"}
          </Button>
        )}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-muted-foreground sm:grid-cols-3">
        {req.event_type && <span className="inline-flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" />{req.event_type}</span>}
        {req.event_date && <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{new Date(req.event_date).toLocaleDateString("it-IT")}</span>}
        {req.guests && <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{req.guests} ospiti</span>}
        {req.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{req.location}</span>}
        {req.budget_eur && <span className="inline-flex items-center gap-1.5"><Euro className="h-3.5 w-3.5" />€{req.budget_eur}</span>}
      </div>

      <p className="mt-3 text-foreground whitespace-pre-wrap">{req.message}</p>

      {(role === "chef" && expanded) && (
        <div className="mt-4 space-y-3 border-t border-border pt-4">
          <div className="flex flex-wrap gap-3 text-sm">
            {req.client_email && <a href={`mailto:${req.client_email}`} className="inline-flex items-center gap-1.5 text-gold hover:underline"><Mail className="h-3.5 w-3.5" />{req.client_email}</a>}
            {req.client_phone && <a href={`tel:${req.client_phone}`} className="inline-flex items-center gap-1.5 text-gold hover:underline"><Phone className="h-3.5 w-3.5" />{req.client_phone}</a>}
          </div>

          {req.chef_reply && (
            <div className="rounded-lg bg-muted/40 p-3">
              <p className="text-xs font-medium text-muted-foreground mb-1">La tua risposta:</p>
              <p className="text-sm whitespace-pre-wrap">{req.chef_reply}</p>
            </div>
          )}

          {req.status !== "closed" && (
            <>
              <Textarea
                rows={3}
                placeholder="Scrivi una risposta…"
                value={reply ?? ""}
                onChange={(e) => setReply?.(e.target.value)}
                maxLength={2000}
              />
              <div className="flex gap-2">
                <Button onClick={onReply} disabled={busy || (reply?.trim().length ?? 0) < 2} className="bg-gold text-gold-foreground hover:bg-gold/90">
                  {busy ? "Invio…" : req.chef_reply ? "Aggiorna risposta" : "Invia risposta"}
                </Button>
                <Button variant="ghost" onClick={onClose}>Chiudi richiesta</Button>
              </div>
            </>
          )}
        </div>
      )}

      {role === "client" && req.chef_reply && (
        <div className="mt-4 rounded-lg border border-gold/30 bg-gold/5 p-3">
          <p className="text-xs font-medium text-gold mb-1">Risposta dello chef:</p>
          <p className="text-sm whitespace-pre-wrap">{req.chef_reply}</p>
        </div>
      )}
    </div>
  );
}